import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { UserAuth } from "./AuthContext";
import { db } from "../lib/Firebase-config";
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { getTheme } from "../theme";

const DarkModeContext = createContext();

export const DarkModeProvider = ({ children }) => {
    const { user } = UserAuth();
    const [darkMode, setDarkMode] = useState(false);
    const [colorPalette, setColorPalette] = useState('emerald');
    const [loading, setLoading] = useState(true);

    // Load preferences from localStorage initially
    useEffect(() => {
        const savedMode = localStorage.getItem('darkMode');
        const savedPalette = localStorage.getItem('colorPalette');

        if (savedMode !== null) {
            setDarkMode(savedMode === 'true');
        }
        if (savedPalette !== null) {
            setColorPalette(savedPalette);
        }
        setLoading(false);
    }, []);

    // Sync with Firebase when user is logged in
    useEffect(() => {
        if (!user?.uid) return;

        const userSettingsRef = doc(db, 'user_settings', user.uid);

        // Real-time listener
        const unsubscribe = onSnapshot(userSettingsRef, (docSnap) => {
            if (docSnap.exists()) {
                const settings = docSnap.data();
                if (settings.darkMode !== undefined) {
                    setDarkMode(settings.darkMode);
                    localStorage.setItem('darkMode', settings.darkMode);
                }
                if (settings.colorPalette) {
                    setColorPalette(settings.colorPalette);
                    localStorage.setItem('colorPalette', settings.colorPalette);
                }
            }
        });

        return () => unsubscribe();
    }, [user]);

    const theme = useMemo(() => getTheme(darkMode ? 'dark' : 'light', colorPalette), [darkMode, colorPalette]);

    const toggleDarkMode = async () => {
        const newMode = !darkMode;
        setDarkMode(newMode);
        localStorage.setItem('darkMode', newMode);

        if (user?.uid) {
            try {
                const userSettingsRef = doc(db, 'user_settings', user.uid);
                await setDoc(userSettingsRef, {
                    darkMode: newMode,
                    updatedAt: new Date().toISOString()
                }, { merge: true });
            } catch (error) {
                console.error('Error saving dark mode:', error);
            }
        }
    };

    const updateColorPalette = async (newPalette) => {
        setColorPalette(newPalette);
        localStorage.setItem('colorPalette', newPalette);

        if (user?.uid) {
            try {
                const userSettingsRef = doc(db, 'user_settings', user.uid);
                await setDoc(userSettingsRef, {
                    colorPalette: newPalette,
                    updatedAt: new Date().toISOString()
                }, { merge: true });
            } catch (error) {
                console.error('Error saving palette:', error);
            }
        }
    };

    return (
        <DarkModeContext.Provider value={{ darkMode, toggleDarkMode, colorPalette, updateColorPalette, loading }}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </DarkModeContext.Provider>
    );
};

export const useDarkMode = () => {
    return useContext(DarkModeContext);
};
